import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Card, Col, Container, Row } from 'react-bootstrap';
import { AppLoader } from '../../Components/common/AppLoader/AppLoader';
import {
  fetchProductsForDisplay,
  getProductIdFromSlug,
  mapProductToCardProps,
  mapProductToDetailProps,
} from '../../api/productApi';
import aboutusBg from '../../assets/images/aboutus-bg.png';
import '../bizaariostore/BizaarioStorePage.css';
import '../home/bizaariostore/bizaariostore.css';
import './productdetails.css';

export const Productdetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [activeImage, setActiveImage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const productId = getProductIdFromSlug(slug);

    const load = async () => {
      setLoading(true);
      setError('');
      setProduct(null);
      setAllProducts([]);

      if (!productId) {
        setError('Product not found.');
        setLoading(false);
        return;
      }

      try {
        const rows = await fetchProductsForDisplay();
        if (cancelled) return;

        const cards = rows.map(mapProductToCardProps);
        const match = rows.find((row) => row.product_id === productId);

        if (!match) {
          setError('Product not found.');
          setLoading(false);
          return;
        }

        const detail = mapProductToDetailProps(match);
        setProduct(detail);
        setActiveImage(detail.image);
        setAllProducts(cards);

        if (detail.slug && detail.slug !== slug) {
          navigate(`/store/${detail.slug}`, { replace: true });
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load product details.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [slug, navigate]);

  const handleProductClick = (item) => {
    navigate(`/store/${item.slug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="product-detail-page">
        <Container className="py-5 text-center">
          <AppLoader message="Loading product details..." variant="page" />
        </Container>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="product-detail-page">
        <Container className="py-5 text-center">
          <p className="text-danger">{error || 'Product not found.'}</p>
          <Button variant="primary" onClick={() => navigate('/store')}>
            Back to Store
          </Button>
        </Container>
      </div>
    );
  }

  return (
    <div className="product-detail-page">
      <section
        className="store-page__hero"
        style={{ backgroundImage: `url(${aboutusBg})` }}
      >
        <div className="store-page__hero-overlay">
          <Container>
            <p className="store-page__hero-label">Bizaario Store</p>
            <h1 className="store-page__hero-title">
              Shop smarter for your health — curated medical supplies and wellness products.
            </h1>
          </Container>
        </div>
      </section>

      <Container fluid className="product-detail__body">
        <section className="product-detail__featured">
          <div className="product-detail__gallery">
            <img
              src={activeImage}
              alt={product.title}
              className="product-detail__main-image"
            />
            {product.images.length > 1 && (
              <div className="product-detail__thumbs">
                {product.images.map((src) => (
                  <button
                    key={src}
                    type="button"
                    className={`product-detail__thumb-btn${activeImage === src ? ' product-detail__thumb-btn--active' : ''}`}
                    onClick={() => setActiveImage(src)}
                  >
                    <img src={src} alt="" className="product-detail__thumb" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="product-detail__content">
            {product.categoryName ? (
              <p className="product-detail__category">{product.categoryName}</p>
            ) : null}
            <h2 className="product-detail__title">{product.title}</h2>
            {product.shortDescription ? (
              <p className="product-detail__desc">{product.shortDescription}</p>
            ) : null}
            {product.longDescription ? (
              <p className="product-detail__desc">{product.longDescription}</p>
            ) : null}

            <div className="product-detail__meta">
              {product.subCategoryName ? (
                <p className="product-detail__meta-item">
                  <span className="product-detail__meta-label">Sub-category: </span>
                  {product.subCategoryName}
                </p>
              ) : null}
              {product.comments ? (
                <p className="product-detail__meta-item">
                  <span className="product-detail__meta-label">Notes: </span>
                  {product.comments}
                </p>
              ) : null}
            </div>

            {(product.videoUrl || product.profilePdf) && (
              <div className="product-detail__links">
                {product.videoUrl ? (
                  <a
                    href={product.videoUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="product-detail__link"
                  >
                    Watch product video
                  </a>
                ) : null}
                {product.profilePdf ? (
                  <a
                    href={product.profilePdf}
                    target="_blank"
                    rel="noreferrer"
                    className="product-detail__link"
                  >
                    View product profile (PDF)
                  </a>
                ) : null}
              </div>
            )}

            <Button className="product-detail__quote-btn">Request a Quote</Button>
          </div>
        </section>

        <section className="product-detail__all-products">
          <h3 className="product-detail__all-title">All Products</h3>
          <p className="product-detail__all-subtitle">
            Browse our full range of medical supplies and wellness products.
          </p>

          <Row xs={1} md={2} lg={3} className="g-4">
            {allProducts.map((item) => (
              <Col key={item.id}>
                <Card
                  className={`bizaario-store__card border-0${item.id === product.id ? ' product-detail__card--active' : ''}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleProductClick(item)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleProductClick(item);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="bizaario-store__card-image-wrapper">
                    <Card.Img
                      variant="top"
                      src={item.image}
                      alt={item.title}
                      className="bizaario-store__card-image"
                    />
                  </div>
                  <Card.Body className="bizaario-store__card-body px-0">
                    <Card.Title className="bizaario-store__card-title">
                      {item.title}
                    </Card.Title>
                    <Card.Text className="bizaario-store__card-desc">
                      {item.description}
                    </Card.Text>
                    <Button
                      className="bizaario-store__card-btn w-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleProductClick(item);
                      }}
                    >
                      Request a Quote
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </section>
      </Container>
    </div>
  );
};
